import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { parseRoomNameFromDealName } from '../dealOptions/dealOptionNameParser';
import { normaliseDealOptionText as normalise } from '../dealOptions/dealOptionsFormatters';

export interface InclusionDealRow {
  deal: OpportunityRelatedDeal;
  dealId: string;
  id: string;
  inclusions: Array<OpportunityRelatedInclusion>;
  name: string;
  roomName: string;
}

export interface InclusionGroup {
  deals: Array<InclusionDealRow>;
  id: string;
  roomName: string;
}

export interface InclusionsViewModel {
  groups: Array<InclusionGroup>;
}

function stringifyId(value: string | number | null | undefined) {
  return value === null || value === undefined || value === '' ? undefined : String(value);
}

function getDealId(deal: OpportunityRelatedDeal) {
  return stringifyId(deal.sfid) ?? stringifyId(deal.id);
}

function getRoomName(deal: OpportunityRelatedDeal) {
  return normalise(deal.room_asset?.name) ?? parseRoomNameFromDealName(deal.name) ?? 'Room not linked';
}

function getRoomGroupId(deal: OpportunityRelatedDeal, roomName: string) {
  return stringifyId(deal.room_asset?.sfid) ?? stringifyId(deal.room_asset?.id) ?? roomName.toLowerCase();
}

function getInclusionKeys(deal: OpportunityRelatedDeal) {
  const keys = [stringifyId(deal.sfid), stringifyId(deal.id)].filter((key): key is string => !!key);
  return Array.from(new Set(keys));
}

function getOptionalInclusionValue(inclusion: OpportunityRelatedInclusion, keys: Array<string>) {
  const record = inclusion as unknown as Record<string, unknown>;

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

export function getInclusionCurrency(inclusion: OpportunityRelatedInclusion, fallback: string) {
  return (
    getOptionalInclusionValue(inclusion, ['currency_iso_code__c', 'currencyisocode', 'currency__c']) ?? fallback
  );
}

export function getInclusionVenues(inclusion: OpportunityRelatedInclusion) {
  return getOptionalInclusionValue(inclusion, [
    'venues__c',
    'venue__c',
    'venues',
    'venue',
    'asset_name__c',
    'asset__name',
    'assetName',
  ]);
}

export function buildInclusionsViewModel({
  deals,
  inclusions,
}: {
  deals: Array<OpportunityRelatedDeal>;
  inclusions: Array<OpportunityRelatedInclusion>;
}): InclusionsViewModel {
  const inclusionsByDealId = new Map<string, Array<OpportunityRelatedInclusion>>();

  for (const inclusion of inclusions) {
    const dealId = normalise(inclusion.deal_offer__c);
    if (!dealId) {
      continue;
    }

    inclusionsByDealId.set(dealId, [...(inclusionsByDealId.get(dealId) ?? []), inclusion]);
  }

  const groupMap = new Map<string, InclusionGroup>();

  deals.forEach((deal, dealIndex) => {
    const roomName = getRoomName(deal);
    const groupId = getRoomGroupId(deal, roomName);
    const group = groupMap.get(groupId) ?? { deals: [], id: groupId, roomName };
    const dealId = getDealId(deal) ?? `deal:${dealIndex}`;
    const inclusionRows = new Map<string, OpportunityRelatedInclusion>();

    for (const key of getInclusionKeys(deal)) {
      for (const inclusion of inclusionsByDealId.get(key) ?? []) {
        const inclusionId = stringifyId(inclusion.id) ?? `${key}:${inclusionRows.size}`;
        inclusionRows.set(inclusionId, inclusion);
      }
    }

    let rowId = dealId;
    let duplicateIndex = 1;
    while (group.deals.some((existingDeal) => existingDeal.id === rowId)) {
      rowId = `${dealId}:${duplicateIndex}`;
      duplicateIndex += 1;
    }

    group.deals.push({
      deal,
      dealId,
      id: rowId,
      inclusions: Array.from(inclusionRows.values()),
      name: normalise(deal.name) ?? dealId,
      roomName,
    });
    groupMap.set(groupId, group);
  });

  return {
    groups: Array.from(groupMap.values()).sort((leftGroup, rightGroup) =>
      leftGroup.roomName.localeCompare(rightGroup.roomName),
    ),
  };
}
